import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { createSmsConfigApi, getSmsConfigsApi, updateSmsConfigApi } from "../api/api";

interface SmsConfig {
  id: number;
  username: string;
  password: string;
  sender_id: string;
  api_url: string;
  status: string;
}

export default function SmsSettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [configs, setConfigs] = useState<SmsConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formState, setFormState] = useState<Partial<SmsConfig>>({
    username: "",
    password: "",
    sender_id: "",
    api_url: "",
    status: "active",
  });

  const isAdmin = (user?.role || "").toString().toLowerCase() === "admin";

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (!isAdmin) {
      navigate("/");
      return;
    }

    const loadConfigs = async () => {
      try {
        setLoading(true);
        const response = await getSmsConfigsApi(user.token);
        const data = response?.data ?? response;
        setConfigs(Array.isArray(data) ? data : []);
      } catch (err: any) {
        console.error("Unable to load SMS configs", err);
        setError(err?.message || "Failed to load SMS configuration");
      } finally {
        setLoading(false);
      }
    };

    loadConfigs();
  }, [isAdmin, navigate, user]);

  const handleChange = (key: keyof SmsConfig, value: string) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
  };

  const validateForm = () => {
    if (!formState.username?.trim()) return "Username is required.";
    if (!formState.password?.trim()) return "Password is required.";
    if (!formState.sender_id?.trim()) return "Sender ID is required.";
    if (!formState.api_url?.trim()) return "API URL is required.";
    if (!formState.status?.trim()) return "Status is required.";
    return null;
  };

  const handleCreate = async () => {
    setError(null);
    setSuccess(null);
    const validationError = validateForm();
    if (validationError) return setError(validationError);
    if (!user?.token) return setError("Not authenticated.");

    try {
      setLoading(true);
      const payload = {
        username: formState.username,
        password: formState.password,
        sender_id: formState.sender_id,
        api_url: formState.api_url,
        status: formState.status,
      };
      const response = await createSmsConfigApi(payload, user.token);
      const created = response?.data ?? response;
      setConfigs((prev) => (Array.isArray(created) ? created : [...prev, created]));
      setSuccess("SMS configuration created successfully.");
      setFormState({ username: "", password: "", sender_id: "", api_url: "", status: "active" });
    } catch (err: any) {
      console.error("Unable to create SMS config", err);
      setError(err?.message || "Failed to create SMS configuration.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (config: SmsConfig) => {
    setError(null);
    setSuccess(null);
    if (!user?.token) return setError("Not authenticated.");

    const validationError = validateForm();
    if (validationError) return setError(validationError);

    try {
      setSavingId(config.id);
      const payload = {
        username: formState.username,
        password: formState.password,
        sender_id: formState.sender_id,
        api_url: formState.api_url,
        status: formState.status,
      };
      const response = await updateSmsConfigApi(config.id, payload, user.token);
      const updated = response?.data ?? response;

      setConfigs((prev) => prev.map((item) => (item.id === config.id ? { ...item, ...updated } : item)));
      setSuccess("SMS configuration updated successfully.");
      setFormState({ username: "", password: "", sender_id: "", api_url: "", status: "active" });
      setSavingId(null);
    } catch (err: any) {
      console.error("Unable to update SMS config", err);
      setError(err?.message || "Failed to update SMS configuration.");
      setSavingId(null);
    }
  };

  const handleEdit = (config: SmsConfig) => {
    setFormState({ ...config });
    setError(null);
    setSuccess(null);
  };

  return (
    <div style={{ padding: "24px" }}>
      <h1 style={{ marginBottom: 20 }}>SMS Settings</h1>
      <div style={{ display: "grid", gap: 20 }}>
        <div style={{ background: "#fff", borderRadius: 14, padding: 24, border: "1px solid #e5e7eb" }}>
          <h2 style={{ marginTop: 0, marginBottom: 16 }}>Create / Update SMS Config</h2>
          {(error || success) && (
            <div style={{ marginBottom: 16 }}>
              {error && <div style={{ color: "#b91c1c" }}>{error}</div>}
              {success && <div style={{ color: "#15803d" }}>{success}</div>}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <label style={labelStyle}>
              Username
              <input
                value={formState.username || ""}
                onChange={(e) => handleChange("username", e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              Password
              <input
                type="password"
                value={formState.password || ""}
                onChange={(e) => handleChange("password", e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              Sender ID
              <input
                value={formState.sender_id || ""}
                onChange={(e) => handleChange("sender_id", e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              API URL
              <input
                value={formState.api_url || ""}
                onChange={(e) => handleChange("api_url", e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              Status
              <select
                value={formState.status || "active"}
                onChange={(e) => handleChange("status", e.target.value)}
                style={inputStyle}
              >
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
            </label>
          </div>

          <div style={{ marginTop: 20, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={handleCreate} disabled={loading} style={buttonStyle}>
              {loading ? "Saving..." : "Save SMS Config"}
            </button>
            <button
              onClick={() => setFormState({ username: "", password: "", sender_id: "", api_url: "", status: "active" })}
              style={{ ...buttonStyle, background: "#f3f4f6", color: "#111827" }}
              type="button"
            >
              Reset
            </button>
          </div>
        </div>

        <div style={{ background: "#fff", borderRadius: 14, padding: 24, border: "1px solid #e5e7eb" }}>
          <h2 style={{ marginTop: 0, marginBottom: 16 }}>Existing Configurations</h2>
          {loading ? (
            <div style={{ color: "#6b7280" }}>Loading SMS settings...</div>
          ) : configs.length === 0 ? (
            <div style={{ color: "#6b7280" }}>No SMS configurations found.</div>
          ) : (
            <div style={{ display: "grid", gap: 16 }}>
              {configs.map((config) => (
                <div key={config.id} style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 18, background: "#f8fafc" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontWeight: 700 }}>{config.sender_id}</div>
                      <div style={{ color: "#6b7280", fontSize: 13 }}>{config.api_url}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button onClick={() => handleEdit(config)} style={ghostButtonStyle} type="button">
                        Edit
                      </button>
                      <button
                        onClick={() => handleUpdate(config)}
                        disabled={savingId === config.id}
                        style={buttonStyle}
                        type="button"
                      >
                        {savingId === config.id ? "Updating..." : "Update"}
                      </button>
                    </div>
                  </div>
                  <div style={{ marginTop: 14, display: "grid", gap: 6 }}>
                    <div><strong>Username:</strong> {config.username}</div>
                    <div><strong>Password:</strong> {config.password}</div>
                    <div><strong>Sender ID:</strong> {config.sender_id}</div>
                    <div><strong>API URL:</strong> {config.api_url}</div>
                    <div><strong>Status:</strong> {config.status}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  fontSize: 14,
  color: "#374151",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 44,
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #d1d5db",
  outline: "none",
  fontSize: 14,
};

const buttonStyle: React.CSSProperties = {
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 10,
  padding: "12px 18px",
  cursor: "pointer",
};

const ghostButtonStyle: React.CSSProperties = {
  background: "transparent",
  color: "#2563eb",
  border: "1px solid #2563eb",
  borderRadius: 10,
  padding: "10px 16px",
  cursor: "pointer",
};
